import { merge, isEmpty, get, find, filter } from 'lodash';
import set from 'lodash/fp/set';

import {
  DEVICE_TYPE,
  REGION_TYPE,
  CONFIG_KEYS,
  CURRENT_ROUTE,
  IS_TABLET,
  PAGE_URL,
  PAGE_QUERY,
  CHECK_AUTH_SUCCESS,
  SET_USER_STATE,
  GET_USER_DETAILS,
  UPDATE_SESSION_EMAIL,
  PAGE_ORIGIN,
  SET_GLOBAL_ERROR,
  REMOVE_GLOBAL_ERROR,
  GET_APPLICATION_LABELS_SUCCESS,
  GET_APPLICATION_PAGE_DATA_SUCCESS,
  GET_ORDER_SUMMARY_DATA_SUCCESS,
  GET_ORDER_SUMMARY_DATA_ERROR,
  HIDE_GLOBAL_ERROR,
  SET_LOADER_STATUS,
  LOAD_COUNTRY_DATA_SUCCESS,
  LOAD_COUNTRY_DATA_FAILURE,
  LOAD_SHOPPING_CART_DATA_SUCCESS,
  LOAD_SHOPPING_CART_FAILURE,
  GET_PROMO_CODE_DETAILS_SUCCESS,
  PLACE_ORDER_SUCCESS,
  SET_COUNTRY_STATUS_SUCCESS,
  SET_COUNTRY_STATUS_FAILURE,
  SEARCH_BAR_REQUEST_SUCCESS,
  SET_EPROC_APP,
  PUNCHOUT_SETUP_FAILURE,
  COUNTRY_CONFIG_SUCCESS,
  ADD_QUOTE_DETAILS,
  ADD_QUOTE_TO_ORDER_ADDRESS_SUCCESS,
  QUOTE_ORDER_SUCCESS,
  RESET_COUPON_ERROR_DATA,
  SYSTEM_NOTIFICATION_SUCCESS,
  SYSTEM_NOTIFICATION_FAILURE,
  SYSTEM_NOTIFICATION_CLOSE,
  SET_FREIGHT_CHARGE_MODAL_STATUS,
  SHORT_TERM_TOKEN_EXPIRED,
  USER_AUTHORIZATION_SUCCESS,
  SET_SHIPPING_INFO,
  USER_AUTHENTICATION,
  USER_AUTHENTICATION_CLEAR,
  SET_END_SESSION_MODAL_STATUS,
  UPDATE_PAGE_DATA_WITH_V2NAV_DATA,
  UPDATE_CUSTOM_PRICING,
  PRODUCT_RECOMMENDATION,
  SET_ANNOUNCEMENT_DATA,
  SET_SPECIAL_ANNOUNCEMENT_DATA,
  CREATE_SETUP_INTENT_ID_SUCCESS,
  CREATE_SETUP_INTENT_ID_FAILURE,
  CLEAR_SETUP_INTENT_ID_DATA,
} from '../constants';

export const initState = {
  deviceType: '',
  activeRegion: '',
  cart: {},
  freightChargeModalStatus: false,
  endSessionModalStatus: false,
  error: [],
};

export const getGlobalErrorData = (state, error) => {
  const errorData = get(state, 'error', []);
  const data = get(error, 'error', {});
  if (isEmpty(data)) {
    return errorData;
  }
  const code = find(
    errorData,
    item => item.code === data.code && item.page === window.location.pathname
  );
  return isEmpty(code)
    ? errorData.concat({ ...error.error, page: window.location.pathname })
    : errorData;
};

export const getRemovedGlobalErrorData = (state, error) => {
  const { code = '' } = error;
  const errorData = get(state, 'error', []);
  return filter(errorData, item => item.code !== code);
};

const setDevice = (state, deviceType) => set('deviceType', deviceType, state);

const setIsTablet = (state, isTablet) => set('isTablet', isTablet, state);

const setConfigKeys = (state, configKeys) => set('configKeys', configKeys, state);

const setRegion = (state, activeRegion) => set('activeRegion', activeRegion, state);

const setPageUrl = (state, pageUrl) => set('pageUrl', pageUrl, state);

const setRoute = (state, pathname) => set('route', pathname, state);

const setPageQuery = (state, pageQuery) => set('pageQuery', pageQuery, state);

const setAuthentication = (state, sessionInfo) => set('sessionInfo', sessionInfo, state);
const setSessionEmail = state => {
  const sessionInfo = get(state, 'sessionInfo');
  return set('sessionInfo', sessionInfo, state);
};

const setUserState = (state, userState) => set('userState', userState, state);

const setUserDetails = (state, userDetails) => set('userDetails', userDetails, state);

const setPageOrigin = (state, origin) => set('pageOrigin', origin, state);

const setGlobalError = (state = initState, error = {}) => {
  const data = getGlobalErrorData(state, error);
  return { ...state, error: data };
};
const removeGlobalError = (state = initState, error) => {
  const data = getRemovedGlobalErrorData(state, error);
  return { ...state, error: data };
};
const setLoaderStatus = (state, status) => set('isAPILoading', status, state);

const setApplicationLabels = (state, data) => {
  const newState = set('labels', data.labels, state);
  return set('config', data.config, newState);
};

const setApplicationPageData = (state, data) => set('pageData', data, state);

const setCountryData = (state, data) => set('countryData', data, state);

const setCountryDataError = (state, error) => set('countryData', error, state);

export const setOrderSummaryData = (state = initState, data = {}) => ({
  ...state,
  cart: { ...state.cart, ...data },
});

export const setOrderSummaryDataError = (state = initState, data = {}) => ({
  ...state,
  ...{ ...merge({}, state, data) },
});

export const setPromoCodeDetailsData = (state = initState, data = {}) => ({
  ...state,
  ...{ promoCodeDetails: { ...data } },
});

export const addShoppingCartData = (state = initState, data = {}, error = null) => ({
  ...state,
  ...{ cart: { ...data, error } },
});

export const errorShoppingCartData = (state = initState, error = null) => ({
  ...state,
  ...{ cart: { error } },
});

export const resetCouponErrorData = (state = initState) => ({
  ...state,
  cart: { ...merge({}, state.cart, { couponErrors: null }) },
});

export const placeOrderSimulateData = (state = initState, data = {}) => ({
  ...state,
  cart: {
    ...merge({}, state.cart, data, { error: null }),
    ...{ shipTo: data.shipTo || {} },
    ...{ billTo: data.billTo || {} },
    ...{ cardDetails: data.cardDetails },
    ...{ poNumber: data.poNumber },
  },
});

export const quoteOrderSimulateData = (state = initState, data = {}) => ({
  ...state,
  quoteData: {
    quotes: {
      ...merge({}, state.quoteData.quotes, data, { error: null }),
      ...{ shipTo: data.shipTo || {} },
      ...{ billTo: data.billTo || {} },
      ...{ cardDetails: data.cardDetails },
      ...{ poNumber: data.poNumber },
    },
  },
});

const setCountryStatus = (state, data) => set('countryStatus', data, state);

const setCountryStatusError = (state, error) => set('countryStatus', error, state);

const setSearchResult = (state, data) => set('searchResult', data, state);

export const setEprocApp = (state = initState) => ({ ...state, isEprocurementApp: true });

const setPunchoutSetupFailure = (state, data) => set('setupFailure', data, state);
const setCountryCommerceConfig = (state, data) => ({
  ...state,
  ...{ countryConfig: { ...data } },
});

export const addQuoteDetailsData = (state = initState, data = {}) => ({
  ...state,
  ...{ quoteData: { ...data } },
});

export const addQuoteToOrderAddressData = (state = initState, data = {}) => ({
  ...state,
  ...{ quoteData: { quotes: { ...data } } },
});

export const setSystemNotificationMessage = (state = initState, data = {}) => ({
  ...state,
  ...{ systemNotification: { notification: { ...data }, isSystemNotificationShow: true } },
});

export const setSystemNotificationMessageError = (state = initState, error = {}) => ({
  ...state,
  ...{ systemNotification: { error: { ...error }, isSystemNotificationShow: false } },
});

export const closeSystemNotification = (state = initState) => ({
  ...state,
  ...{
    systemNotification: {
      ...merge({}, state.systemNotification, { isSystemNotificationShow: false }),
    },
  },
});
export const setFreightChargeModalStatus = (status, state = initState) => ({
  ...state,
  ...{ freightChargeModalStatus: status },
});
export const setShortTermTokenExpired = (state = initState, data = {}) => ({
  ...state,
  shortTermToken: { ...data },
});
export const setPasswordPromptData = (state = initState, data = {}) => ({
  ...state,
  PasswordPromptData: { ...data },
});
export const setShippingInfo = (state = initState, data = []) => ({
  ...state,
  shippingInfo: [...data],
});

export const setUserAuthentication = (state = initState, data = {}) => ({
  ...state,
  userAuthentication: { ...data },
});

export const setEndSessionModalStatus = (state = initState, data) => ({
  ...state,
  ...{ endSessionModalStatus: data },
});

export const updatePageDataWithNavV2Data = (state = initState, data) => ({
  ...state,
  pageData: {
    ...state.pageData,
    'Navigation Content': {
      ...state.pageData['Navigation Content'],
      items: data,
    },
  },
});

export const productRecommendations = (state = initState, data = []) => ({
  ...state,
  productRecommendationData: data,
});

export const setAnnouncement = (state = initState, data = []) => ({
  ...state,
  announcements: data,
});
export const setSpecialAnnouncement = (state = initState, data = []) => ({
  ...state,
  specialAnnouncement: data,
});

export const createSetupIntentIdSuccess = (state = initState, data = {}) => ({
  ...state,
  ...{ stripeData: data },
});
export const clearSetupIntentIdData = (state = initState, data = {}) => ({
  ...state,
  ...{ stripeData: data },
});

export const createSetupIntentIdFailure = (state = initState, error = {}) => ({
  ...state,
  ...error,
});

/* eslint-disable */
export default (state = initState, action = {}) => {
  switch (action.type) {
    case DEVICE_TYPE:
      return setDevice(state, get(action, 'deviceType'));
    case IS_TABLET:
      return setIsTablet(state, get(action, 'isTablet'));
    case REGION_TYPE:
      return setRegion(state, get(action, 'activeRegion'));
    case CONFIG_KEYS:
      return setConfigKeys(state, get(action, 'configKeys'));
    case CURRENT_ROUTE:
      return setRoute(state, get(action, 'pathname'));
    case PAGE_URL:
      return setPageUrl(state, get(action, 'pageUrl'));
    case PAGE_QUERY:
      return setPageQuery(state, get(action, 'pageQuery'));
    case CHECK_AUTH_SUCCESS:
      return setAuthentication(state, get(action, 'sessionInfo'));
    case UPDATE_SESSION_EMAIL:
      return setSessionEmail(state);
    case SET_USER_STATE:
      return setUserState(state, get(action, 'userState'));
    case GET_USER_DETAILS:
      return setUserDetails(state, get(action, 'userDetails'));
    case PAGE_ORIGIN:
      return setPageOrigin(state, get(action, 'origin'));
    case SET_GLOBAL_ERROR:
      return setGlobalError(state, get(action, 'data'));
    case REMOVE_GLOBAL_ERROR:
      return removeGlobalError(state, get(action, 'data'));
    case HIDE_GLOBAL_ERROR:
      return setGlobalError(state, get(action, 'error'));
    case GET_APPLICATION_LABELS_SUCCESS:
      return setApplicationLabels(state, get(action, 'data'));
    case GET_APPLICATION_PAGE_DATA_SUCCESS:
      return setApplicationPageData(state, get(action, 'data'));
    case GET_ORDER_SUMMARY_DATA_SUCCESS:
      return setOrderSummaryData(state, get(action, ['data', 'orderSummaryData'], {}));
    case GET_ORDER_SUMMARY_DATA_ERROR:
      return setOrderSummaryDataError(state, get(action, 'data', {}));
    case GET_PROMO_CODE_DETAILS_SUCCESS:
      return setPromoCodeDetailsData(state, get(action, 'data', {}));
    case SET_LOADER_STATUS:
      return setLoaderStatus(state, get(action, 'status'));
    case LOAD_COUNTRY_DATA_SUCCESS:
      return setCountryData(state, get(action, 'data', {}));
    case LOAD_COUNTRY_DATA_FAILURE:
      return setCountryDataError(state, get(action, 'error', {}));
    case LOAD_SHOPPING_CART_DATA_SUCCESS:
      return addShoppingCartData(state, get(action, 'data', {}), get(action, 'error', null));
    case LOAD_SHOPPING_CART_FAILURE:
      return errorShoppingCartData(state, get(action, 'error', {}));
    case PLACE_ORDER_SUCCESS:
      return placeOrderSimulateData(state, get(action, 'data', {}));
    case QUOTE_ORDER_SUCCESS:
      return quoteOrderSimulateData(state, get(action, 'data', {}));
    case SET_COUNTRY_STATUS_SUCCESS:
      return setCountryStatus(state, get(action, 'data', {}));
    case SET_COUNTRY_STATUS_FAILURE:
      return setCountryStatusError(state, get(action, 'error', {}));
    case SEARCH_BAR_REQUEST_SUCCESS:
      return setSearchResult(state, get(action, 'data', {}));
    case SET_EPROC_APP:
      return setEprocApp(state);
    case PUNCHOUT_SETUP_FAILURE:
      return setPunchoutSetupFailure(
        state,
        get(action, 'data', { status: false, code: 400, redirectUrl: '' })
      );
    case COUNTRY_CONFIG_SUCCESS:
      return setCountryCommerceConfig(state, get(action, 'data', {}));
    case ADD_QUOTE_DETAILS:
      return addQuoteDetailsData(state, get(action, 'data', {}));
    case ADD_QUOTE_TO_ORDER_ADDRESS_SUCCESS:
      return addQuoteToOrderAddressData(state, get(action, 'data', {}));
    case RESET_COUPON_ERROR_DATA:
      return resetCouponErrorData(state);
    case SYSTEM_NOTIFICATION_SUCCESS:
      return setSystemNotificationMessage(state, get(action, 'data', {}));
    case SYSTEM_NOTIFICATION_FAILURE:
      return setSystemNotificationMessageError(state, get(action, 'error', {}));
    case SYSTEM_NOTIFICATION_CLOSE:
      return closeSystemNotification(state);
    case SET_FREIGHT_CHARGE_MODAL_STATUS:
      return setFreightChargeModalStatus(get(action, 'status', false), state);
    case SHORT_TERM_TOKEN_EXPIRED:
      return setShortTermTokenExpired(state, get(action, 'data', {}));
    case USER_AUTHORIZATION_SUCCESS:
      return setPasswordPromptData(state, get(action, 'data', {}));
    case SET_SHIPPING_INFO:
      return setShippingInfo(state, get(action, 'data', []));
    case USER_AUTHENTICATION_CLEAR:
    case USER_AUTHENTICATION:
      return setUserAuthentication(state, get(action, 'data', {}));
    case SET_END_SESSION_MODAL_STATUS:
      return setEndSessionModalStatus(state, get(action, 'data', false));
    case UPDATE_PAGE_DATA_WITH_V2NAV_DATA:
      return updatePageDataWithNavV2Data(state, get(action, 'data', {}));
    case UPDATE_CUSTOM_PRICING:
    case PRODUCT_RECOMMENDATION:
      return productRecommendations(state, get(action, 'data', []));
    case SET_ANNOUNCEMENT_DATA:
      return setAnnouncement(state, get(action, 'data', []));
    case SET_SPECIAL_ANNOUNCEMENT_DATA:
      return setSpecialAnnouncement(state, get(action, 'data', []));
    case CREATE_SETUP_INTENT_ID_SUCCESS:
      return createSetupIntentIdSuccess(state, get(action, 'data', {}));
    case CREATE_SETUP_INTENT_ID_FAILURE:
      return createSetupIntentIdFailure(state, get(action, 'error', {}));
    case CLEAR_SETUP_INTENT_ID_DATA:
      return clearSetupIntentIdData(state, get(action, 'data', {}));
    default:
      return state;
  }
};

/* eslint-enable */
